import { create } from 'zustand';
import allData from '../data/all_data.json';
import useModalStore from './modalStore';

// We need to explicitly copy the data here to ensure it's actually loaded
const productsData = Object.assign({}, allData);

// Log the data to confirm it's loaded
console.log('Product data loaded:', {
  sheets: Object.keys(productsData),
  rowCounts: Object.keys(productsData).map(sheet => `${sheet}: ${productsData[sheet].length}`)
});

// Define the main product categories based on the Excel sheets
const MAIN_CATEGORIES = [
  {
    id: 'meet_neva',
    name: 'How To Configure',
    description: 'Learn about Neva products and features',
    icon: '🧠',
    // No sheet or filter - this is an informational section only
  },
  {
    id: 'poles',
    name: 'Poles',
    description: 'Choose from a variety of poles for your big bag system',
    icon: '🏗️',
    sheet: 'Poles & Spare Parts',
    filter: item => item["Category (Filter/Sort!)"]?.includes('Pole')
  },
  {
    id: 'decks',
    name: 'Decks',
    description: 'Select the right deck for your application',
    icon: '🏢',
    sheet: 'Decks'
  },
  {
    id: 'unloading_stations',
    name: 'Unloading Stations',
    description: 'Configure your unloading station with the right features',
    icon: '<img src="https://i.imgur.com/A7PYa9k.png" alt="Unloading Station" />',
    sheet: 'Unloading Stations'
  }
];

// Helper to classify products into subcategories based on their category field
const classifyProducts = () => {
  const subcategories = {};
  
  // Process each sheet
  Object.keys(productsData).forEach(sheetName => {
    // Find the main category this sheet belongs to
    const mainCategory = MAIN_CATEGORIES.find(cat => cat.sheet === sheetName);
    if (!mainCategory) return;
    
    // Initialize subcategories for this main category if not already done
    if (!subcategories[mainCategory.id]) {
      subcategories[mainCategory.id] = new Set();
    }
    
    // Collect all subcategories from this sheet
    productsData[sheetName].forEach(product => {
      if (product["Category (Filter/Sort!)"]) {
        const category = product["Category (Filter/Sort!)"];
        if (typeof category === 'string' && category.trim() !== "" && category !== " ") {
          subcategories[mainCategory.id].add(category);
        }
      }
    });
  });
  
  // Convert Sets to arrays and create proper objects
  const result = {};
  Object.keys(subcategories).forEach(mainCategoryId => {
    result[mainCategoryId] = Array.from(subcategories[mainCategoryId])
      .map(name => ({
        id: name.replace(/\s+/g, '_').toLowerCase(),
        name: name,
        parentCategory: mainCategoryId
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  });
  
  return result;
};

// Extract products and organize them by main category and subcategory
const organizeProducts = () => {
  const organizedProducts = {};
  
  // Initialize all categories
  MAIN_CATEGORIES.forEach(category => {
    organizedProducts[category.id] = [];
  });
  
  // Process each sheet
  Object.keys(productsData).forEach(sheetName => {
    // Find all main categories this sheet belongs to (could be multiple with filters)
    const relevantCategories = MAIN_CATEGORIES.filter(cat => cat.sheet === sheetName);
    if (relevantCategories.length === 0) return;
    
    // Process products in this sheet
    productsData[sheetName].forEach(item => {
      // Skip items without article number or name
      if (!item["Art. no."] || !item["Article"]) return;
      
      // Only process items with string categories
      const category = item["Category (Filter/Sort!)"];
      if (typeof category !== 'string' || category.trim() === "" || category === " ") return;
      
      // Find which main category this item belongs to (using filter if present)
      let targetCategory = null;
      for (const mainCategory of relevantCategories) {
        if (mainCategory.filter) {
          // Apply the filter to determine category
          if (mainCategory.filter(item)) {
            targetCategory = mainCategory;
            break;
          }
        } else {
          // No filter means all items from this sheet go to this category
          targetCategory = mainCategory;
          break;
        }
      }
      
      if (!targetCategory) return; // Skip if item doesn't match any category filters
      
      // Extract product attributes based on the item name and category
      const attributes = extractProductAttributes(item, targetCategory.id);
      
      // Create quantity options based on pricing tiers
      const quantities = [];
      const tierColumns = Object.keys(item).filter(key => 
        key.match(/^\d+-\d+$/) && item[key] !== null
      );
      
      // Add each available quantity tier as a choice
      tierColumns.forEach(tier => {
        // Regular (client) price
        const clientPrice = item[tier];
        
        // Partner price (with discount)
        const partnerPrice = item[`Partner Price ${tier}`] || 
                            (item['Partner Discount'] ? clientPrice * (1 - item['Partner Discount']) : clientPrice);
        
        quantities.push({
          id: tier,
          name: tier,
          clientPrice: clientPrice,
          partnerPrice: partnerPrice,
          price: clientPrice // Default to client price
        });
      });
      
      // Convert article number to string if it's a number
      const articleId = typeof item["Art. no."] === 'number' 
        ? item["Art. no."].toString() 
        : item["Art. no."];
        
      // Determine base price
      const basePrice = 
        (item["1-50"] !== null ? item["1-50"] : null) || 
        (item["1-10"] !== null ? item["1-10"] : null) || 
        (item["1-200"] !== null ? item["1-200"] : null) || 
        0;
        
      // Add the product to the organized products
      organizedProducts[targetCategory.id].push({
        id: articleId,
        name: item["Article"],
        category: category,
        subcategoryId: category.replace(/\s+/g, '_').toLowerCase(),
        mainCategoryId: targetCategory.id,
        sheet: sheetName,
        basePrice,
        quantities,
        attributes,
        description: `${item["Article"]} - ${category}`
      });
    });
  });
  
  return organizedProducts;
};

// Extract specific attributes from product names and categories based on the category
const extractProductAttributes = (item, categoryId) => {
  const attributes = {};
  const name = item["Article"];
  const category = item["Category (Filter/Sort!)"];
  
  switch(categoryId) {
    case 'poles':
      // Example: "Flow Pole Galv. Steel 60*2,5mm |1180mm | Small"
      if (name && name.includes('Pole')) {
        // Extract pole type
        if (name.includes('Flow Pole')) {
          attributes.poleType = 'Flow Pole';
        } 
        else if (name.includes('Lockhead Pole')) {
          attributes.poleType = 'Lockhead Pole';
        }
        else if (name.includes('Rolling Springhead Pole')) { 
          // Make sure "Rolling Springhead" is checked BEFORE "Springhead"
          attributes.poleType = 'Rolling Springhead Pole';
        }
        else if (name.includes('Springhead Pole')) {
          attributes.poleType = 'Springhead Pole';
        }
        
        // Extract material type
        if (name.includes('Galv. Steel')) attributes.material = 'Galvanized Steel';
        else if (name.includes('Stainless Steel')) attributes.material = 'Stainless Steel';
        
        // Extract thickness
        const thicknessMatch = name.match(/(\d+)[*](\d+[.,]\d+)mm/);
        if (thicknessMatch) attributes.thickness = `${thicknessMatch[1]}*${thicknessMatch[2]}mm`;
        
        // Extract size/length - handle ranges first, then single values
        const rangeMatch = name.match(/\|\s*(\d+[-]\d+)mm/);
        if (rangeMatch) {
          // Use the range as the size (e.g., "1000-1300mm")
          attributes.size = rangeMatch[1] + 'mm';
        } else {
          // Handle single length values
          const sizeMatch = name.match(/\|(\d+)mm\s*\|?/);
          if (sizeMatch) attributes.size = `${sizeMatch[1]}mm`;
          else if (name.includes('Small')) attributes.size = 'Small';
          else if (name.includes('Medium')) attributes.size = 'Medium';
          else if (name.includes('Large')) attributes.size = 'Large';
        }
      }
      break;

    case 'decks':
      // Example: "Neva Deck | Standard"
      attributes.type = name.includes('|') ? name.split('|')[1].trim() : name;
      
      if (name.includes('Stainless Steel')) attributes.material = 'Stainless Steel';
      else attributes.material = 'Standard';
      
      if (name.includes('Food Grade')) attributes.grade = 'Food Grade';
      else attributes.grade = 'Standard';
      
      if (name.includes('open sides')) attributes.sides = 'Open Sides';
      else attributes.sides = 'Standard';
      
      if (name.includes('No Slide')) attributes.slide = 'No Slide';
      else attributes.slide = 'With Slide';
      
      break;
      
    case 'unloading_stations':
      // Example: "Unloading Station Frame Base"
      if (name.includes('Frame Base')) {
        attributes.type = 'Frame Base';
      } else if (name.includes('Feet set')) {
        attributes.type = 'Feet Set';
        
        if (name.includes('Small')) attributes.size = 'Small';
        else if (name.includes('Medium')) attributes.size = 'Medium';
        else if (name.includes('Large')) attributes.size = 'Large';
        
        // Extract height range
        const heightMatch = name.match(/WH\s*(\d+[-]\d+)mm/);
        if (heightMatch) attributes.heightRange = heightMatch[1] + 'mm';
      } else if (name.includes('Add-on')) {
        attributes.type = 'Add-on';
        attributes.addonType = name.replace('Add-on', '').trim();
      }
      
      if (name.includes('Stainless Steel')) attributes.material = 'Stainless Steel';
      else attributes.material = 'Standard';

      break;
  }
  
  return attributes;
};

// Prepare the data
const subcategories = classifyProducts();
const organizedProducts = organizeProducts();

// Precompute attribute-product mappings
const createAttributeProductMap = () => {
  const attributeMap = {};
  
  Object.keys(organizedProducts).forEach(mainCategoryId => {
    attributeMap[mainCategoryId] = {};
    
    organizedProducts[mainCategoryId].forEach(product => {
      Object.entries(product.attributes || {}).forEach(([attrName, attrValue]) => {
        if (!attributeMap[mainCategoryId][attrName]) {
          attributeMap[mainCategoryId][attrName] = {};
        }
        
        if (!attributeMap[mainCategoryId][attrName][attrValue]) {
          attributeMap[mainCategoryId][attrName][attrValue] = [];
        }
        
        attributeMap[mainCategoryId][attrName][attrValue].push(product.id);
      });
    });
  });
  
  return attributeMap;
};

const attributeProductMap = createAttributeProductMap();

// Initialize the store with the extracted data
const useConfiguratorStore = create((set, get) => ({
  // Main product categories
  mainCategories: MAIN_CATEGORIES,
  
  // Subcategories organized by main category
  subcategories,
  
  // Products organized by main category
  organizedProducts,
  
  // Attribute to product mapping for fast lookups
  attributeProductMap,

  // Price mode - client (default)
  priceMode: 'client',

  // Fair mode - shows simplified pricing without partner/client distinction
  fairModeEnabled: localStorage.getItem('fairMode') === 'true',

  // Discount feature state (default to enabled when not previously set)
  discountEnabled: (() => {
    const storedValue = localStorage.getItem('discountEnabled');
    if (storedValue === null) {
      localStorage.setItem('discountEnabled', 'true');
      return true;
    }
    return storedValue === 'true';
  })(),
  discountPercentage: 0,

  // Sidebar collapse state
  sidebarCollapsed: localStorage.getItem('sidebarCollapsed') === 'true',

  // Show collapse button in sidebar
  showCollapseButton: localStorage.getItem('showCollapseButton') !== 'false',

  // Navigation visibility preferences
  navVisibility: (() => {
    const stored = localStorage.getItem('navVisibility');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return {
          announcements: true,
          configurator: true,
          clientProtection: true,
          poleCalculator: true,
          shippingCalculator: true,
          polesProductPage: true,
          decksProductPage: true,
          unloadingStationsProductPage: true,
          bigBagsProductPage: true,
          sparePartsProductPage: true,
          youtube: true,
          whatsapp: true,
          support: true,
          ...parsed
        };
      } catch (e) {
        console.error('Failed to parse navVisibility from localStorage:', e);
      }
    }
    return {
      announcements: true,
      configurator: true,
      clientProtection: true,
      poleCalculator: true,
      shippingCalculator: true,
      polesProductPage: true,
      decksProductPage: true,
      unloadingStationsProductPage: true,
      bigBagsProductPage: true,
      sparePartsProductPage: true,
      youtube: true,
      whatsapp: true,
      support: true
    };
  })(),

  // Calculated length information from pole calculator
  calculatedLengthInfo: null,
  
  // Cart/quote items
  cartItems: [],
  
  // Track recent successful copy operations for showing success message
  recentCopySuccess: null,
  
  // Filter the products based on selected categories
  getFilteredProducts: () => {
    const { 
      selectedMainCategory, 
      selectedSubcategory, 
      organizedProducts, 
      selectedAttributes 
    } = get();
    
    if (!selectedMainCategory) return [];
    
    let products = organizedProducts[selectedMainCategory] || [];
    
    if (selectedSubcategory) {
      products = products.filter(product => 
        product.subcategoryId === selectedSubcategory
      );
    }
    
    return products;
  },
  
  // Get products matching current attribute selections
  getProductsByAttributes: () => {
    const { 
      selectedMainCategory, 
      selectedSubcategory,
      selectedAttributes, 
      attributeProductMap, 
      organizedProducts 
    } = get();
    
    if (!selectedMainCategory || Object.keys(selectedAttributes).length === 0) {
      return get().getFilteredProducts();
    }
    
    // Start with all products in category/subcategory
    const categoryProducts = get().getFilteredProducts();
    const categoryProductIds = new Set(categoryProducts.map(p => p.id));
    
    let matchingProductIds = null;
    
    // Find products matching all attribute criteria
    Object.entries(selectedAttributes).forEach(([attrName, attrValue]) => {
      if (!attributeProductMap[selectedMainCategory]?.[attrName]?.[attrValue]) {
        return;
      }
      
      const attrMatchingIds = new Set(
        attributeProductMap[selectedMainCategory][attrName][attrValue]
      );
      
      // Intersect with category products
      const filteredIds = [...attrMatchingIds].filter(id => categoryProductIds.has(id));
      
      // First attribute - initialize matches set
      if (matchingProductIds === null) {
        matchingProductIds = new Set(filteredIds);
      } else {
        // Subsequent attributes - intersect with previous matches
        matchingProductIds = new Set(
          [...matchingProductIds].filter(id => filteredIds.includes(id))
        );
      }
    });
    
    // If no attributes matched or all got filtered out
    if (!matchingProductIds || matchingProductIds.size === 0) {
      return [];
    }
    
    // Convert IDs back to product objects
    return categoryProducts.filter(product => matchingProductIds.has(product.id));
  },
  
  // Current selection state
  selectedMainCategory: 'meet_neva',
  selectedSubcategory: null,
  selectedProduct: null,
  selectedQuantity: null,
  selectedAttributes: {},
  
  
  // Current quote information
  quote: {
    subtotal: 0,
    discount: 0,
    tax: 0,
    total: 0,
    lineItems: [],
  },
  
  // Actions for main category selection
  setMainCategory: (categoryId) => 
    set(state => {
      // Keep the cartItems intact when changing categories
      const newState = {
        selectedMainCategory: categoryId,
        selectedSubcategory: null,
        selectedProduct: null,
        selectedQuantity: null,
        selectedAttributes: {}
      };
      
      // Clear calculated length info when switching away from poles
      if (categoryId !== 'poles') {
        newState.calculatedLengthInfo = null;
      }
      
      return newState;
    }),
  
  // Actions for subcategory selection
  setSubcategory: (subcategoryId) => 
    set({
      selectedSubcategory: subcategoryId,
      selectedProduct: null,
      selectedQuantity: null,
      selectedAttributes: {}
    }),
  
  // Set calculated length information from pole calculator
  setCalculatedLengthInfo: (info) =>
    set({ calculatedLengthInfo: info }),

  // Action for product selection
  setSelectedProduct: (productId) => 
    set(state => {
      const { organizedProducts, selectedMainCategory } = state;
      
      if (!selectedMainCategory || !organizedProducts[selectedMainCategory]) {
        return { selectedProduct: null };
      }
      
      const product = organizedProducts[selectedMainCategory].find(p => p.id === productId);
      
      if (!product) {
        return { selectedProduct: null };
      }
      
      // Set default quantity if available
      let selectedQuantity = null;
      if (product.quantities && product.quantities.length > 0) {
        selectedQuantity = product.quantities[0].id;
      }
      
      // Auto-calculate quote after selection
      const updatedState = {
        selectedProduct: productId,
        selectedQuantity,
        // Initialize with the product's attributes
        selectedAttributes: { ...product.attributes }
      };
      
      return updatedState;
    }),
  
  // Action for quantity selection
  setSelectedQuantity: (quantityId) =>
    set(state => {
      const newState = { selectedQuantity: quantityId };
      
      // Auto-calculate quote immediately
      setTimeout(() => get().calculateQuote(), 0);
      
      return newState;
    }),
    
  // Toggle between partner and client price modes
  togglePriceMode: () =>
    set(state => {
      // Don't allow toggle if fair mode is enabled
      if (state.fairModeEnabled) {
        return state;
      }

      const newMode = state.priceMode === 'partner' ? 'client' : 'partner';
      
      // Update all product quantities to use the correct price
      const updatedOrganizedProducts = {};
      
      Object.entries(state.organizedProducts).forEach(([categoryId, products]) => {
        updatedOrganizedProducts[categoryId] = products.map(product => {
          if (!product.quantities) return product;
          
          const updatedQuantities = product.quantities.map(q => ({
            ...q,
            price: newMode === 'client' ? q.clientPrice : q.partnerPrice
          }));
          
          return {
            ...product,
            quantities: updatedQuantities,
            basePrice: updatedQuantities[0]?.price || product.basePrice
          };
        });
      });
      
      // Update existing cart items with new pricing
      const updatedCartItems = state.cartItems.map(cartItem => {
        // Find the product in the updated organized products
        const categoryProducts = updatedOrganizedProducts[cartItem.category] || [];
        const product = categoryProducts.find(p => p.id === cartItem.product.id);
        
        if (!product || !product.quantities) {
          return cartItem; // Return original if product not found
        }
        
        // Find the quantity tier that matches the cart item's tier
        const quantityTier = product.quantities.find(q => q.id === cartItem.quantityTierId);
        
        if (!quantityTier) {
          return cartItem; // Return original if tier not found
        }
        
        // Update the cart item with new pricing
        const newUnitPrice = quantityTier.price;
        const newTotalPrice = newUnitPrice * cartItem.quantity;
        
        return {
          ...cartItem,
          unitPrice: newUnitPrice,
          totalPrice: newTotalPrice,
          priceModeUsed: newMode,
          product: product // Update product reference with new pricing
        };
      });
      
      // Auto-recalculate cart total with new prices
      setTimeout(() => get().calculateCartTotal(), 0);
      
      return {
        priceMode: newMode,
        organizedProducts: updatedOrganizedProducts,
        cartItems: updatedCartItems
      };
    }),

  // Toggle Fair Mode - simplified pricing without partner/client distinction
  toggleFairMode: () =>
    set(state => {
      const newFairMode = !state.fairModeEnabled;
      localStorage.setItem('fairMode', newFairMode);

      // If enabling fair mode, force to client pricing
      if (newFairMode && state.priceMode !== 'client') {
        // Trigger price mode change to client
        setTimeout(() => {
          const { priceMode, togglePriceMode } = get();
          if (priceMode !== 'client') {
            togglePriceMode();
          }
        }, 0);
      }

      return {
        fairModeEnabled: newFairMode
      };
    }),

  // Set discount percentage (0-100)
  setDiscountPercentage: (percentage) =>
    set({
      discountPercentage: Math.min(Math.max(0, percentage), 100)
    }),

  // Clear discount
  clearDiscount: () =>
    set({ discountPercentage: 0 }),

  // Toggle discount enabled state
  toggleDiscountEnabled: () =>
    set(state => {
      const newEnabled = !state.discountEnabled;
      localStorage.setItem('discountEnabled', newEnabled);

      // Clear discount when disabling
      if (!newEnabled) {
        return {
          discountEnabled: newEnabled,
          discountPercentage: 0
        };
      }

      return {
        discountEnabled: newEnabled
      };
    }),

  // Toggle sidebar collapsed state
  toggleSidebarCollapsed: () =>
    set(state => {
      const newCollapsed = !state.sidebarCollapsed;
      localStorage.setItem('sidebarCollapsed', newCollapsed);
      return { sidebarCollapsed: newCollapsed };
    }),

  // Toggle show collapse button in sidebar
  toggleShowCollapseButton: () =>
    set(state => {
      const newShow = !state.showCollapseButton;
      localStorage.setItem('showCollapseButton', newShow);
      return { showCollapseButton: newShow };
    }),

  // Toggle visibility for a specific navigation item
  toggleNavItemVisibility: (itemKey) =>
    set(state => {
      const newVisibility = {
        ...state.navVisibility,
        [itemKey]: !state.navVisibility[itemKey]
      };
      try {
        localStorage.setItem('navVisibility', JSON.stringify(newVisibility));
      } catch (e) {
        console.error('Failed to save navigation visibility to localStorage:', e);
      }
      return { navVisibility: newVisibility };
    }),

  // Reset navigation visibility to default
  resetNavVisibility: () =>
    set(() => {
      const defaultVisibility = {
        announcements: true,
        configurator: true,
        clientProtection: true,
        poleCalculator: true,
        shippingCalculator: true,
        polesProductPage: true,
        decksProductPage: true,
        unloadingStationsProductPage: true,
        bigBagsProductPage: true,
        sparePartsProductPage: true,
        youtube: true,
        whatsapp: true,
        support: true
      };
      try {
        localStorage.setItem('navVisibility', JSON.stringify(defaultVisibility));
      } catch (e) {
        console.error('Failed to save navigation visibility to localStorage:', e);
      }
      return { navVisibility: defaultVisibility };
    }),

  // Action for attribute selection
  setSelectedAttribute: (attributeName, value) =>
    set(state => {
      // Update the attribute immediately
      const newState = {
        selectedAttributes: {
          ...state.selectedAttributes,
          [attributeName]: value
        }
      };
      
      // Remove setTimeout to prevent race conditions
      // Auto-selection will be handled by components using useEffect
      
      return newState;
    }),

  
  // Calculate the current quote for the selected product(s)
  calculateQuote: () =>
    set(state => {
      const { 
        selectedProduct, 
        selectedQuantity, 
        selectedMainCategory,
        organizedProducts,
        cartItems
      } = state;
      
      // If cart items exist, we keep those and just use their total for the quote
      if (cartItems.length > 0) {
        // We still need to update the lineItems for the current product selection
        if (selectedProduct && selectedMainCategory && selectedQuantity) {
          // Find the selected product
          const products = organizedProducts[selectedMainCategory] || [];
          const product = products.find(p => p.id === selectedProduct);
          
          if (product) {
            // Find the selected quantity
            const quantityTier = product.quantities.find(q => q.id === selectedQuantity);
            if (quantityTier) {
              // Extract exact quantity
              const parseQuantityRange = (range) => {
                if (!range) return { min: 1, max: 1 };
                const parts = range.split('-');
                return {
                  min: parseInt(parts[0], 10),
                  max: parts.length > 1 ? parseInt(parts[1], 10) : parseInt(parts[0], 10)
                };
              };
              
              // Determine quantity
              const rangeData = parseQuantityRange(quantityTier.name);
              const exactQuantity = rangeData.min;
              
              // Calculate unit price
              const unitPrice = quantityTier.price;
              
              // Calculate the subtotal based on quantity
              const subtotal = unitPrice * exactQuantity;
              
              // Calculate tax and total from all cart items
              const cartSubtotal = cartItems.reduce((sum, item) => sum + item.totalPrice, 0);
              const tax = cartSubtotal * 0.21; // 21% VAT
              const total = cartSubtotal + tax;
              
              // Create the line items for the currently selected product
              return {
                quote: {
                  subtotal: cartSubtotal,
                  tax,
                  total,
                  lineItems: [{
                    name: product.name,
                    price: unitPrice,
                    quantity: exactQuantity,
                    totalPrice: subtotal,
                    product: product
                  }]
                }
              };
            }
          }
        }
        
        // If we don't have a selection but have cart items, just update the cart totals
        const cartSubtotal = cartItems.reduce((sum, item) => sum + item.totalPrice, 0);
        const tax = cartSubtotal * 0.21; // 21% VAT
        const total = cartSubtotal + tax;
        
        return {
          quote: {
            subtotal: cartSubtotal,
            tax,
            total,
            lineItems: [] // Empty line items for the current selection
          }
        };
      }
      
      
      // If no cart items, calculate for the current selection only
      if (!selectedProduct || !selectedMainCategory || !selectedQuantity) {
        return { 
          quote: {
            subtotal: 0,
            tax: 0,
            total: 0,
            lineItems: []
          }
        };
      }
      
      // Find the selected product
      const products = organizedProducts[selectedMainCategory] || [];
      const product = products.find(p => p.id === selectedProduct);
      
      if (!product) {
        return { 
          quote: {
            subtotal: 0,
            tax: 0,
            total: 0,
            lineItems: []
          }
        };
      }
      
      // Find the selected quantity
      const quantityTier = product.quantities.find(q => q.id === selectedQuantity);
      if (!quantityTier) {
        return { 
          quote: {
            subtotal: 0,
            tax: 0,
            total: 0,
            lineItems: []
          }
        };
      }
      
      // Extract exact quantity if available (for custom quantity input)
      const parseQuantityRange = (range) => {
        if (!range) return { min: 1, max: 1 };
        const parts = range.split('-');
        return {
          min: parseInt(parts[0], 10),
          max: parts.length > 1 ? parseInt(parts[1], 10) : parseInt(parts[0], 10)
        };
      };
      
      // Determine quantity - either use custom input value or average of range
      const rangeData = parseQuantityRange(quantityTier.name);
      // Use the minimum in the range as default quantity
      const exactQuantity = rangeData.min;
      
      // Calculate unit price from the tier
      const unitPrice = quantityTier.price;
      
      // Calculate the subtotal based on quantity
      const subtotal = unitPrice * exactQuantity;
      
      // Create the line items
      const lineItems = [
        {
          name: product.name,
          price: unitPrice,
          quantity: exactQuantity,
          totalPrice: subtotal,
          product: product
        }
      ];
      
      // Calculate tax and total
      const tax = subtotal * 0.21; // 21% VAT
      const total = subtotal + tax;
      
      return {
        quote: {
          subtotal,
          tax,
          total,
          lineItems
        }
      };
    }),
  
  // Helper function to find optimal pricing tier based on quantity
  determineOptimalTier: (product, quantity) => {
    if (!product.quantities || product.quantities.length === 0) return null;
    
    // Parse and sort tiers by minimum quantity
    const parsedTiers = product.quantities.map(tier => {
      const range = tier.id.split('-');
      return {
        ...tier,
        min: parseInt(range[0], 10),
        max: range.length > 1 ? parseInt(range[1], 10) : Infinity
      };
    }).sort((a, b) => a.min - b.min);
    
    // Find the appropriate tier for the given quantity
    for (const tier of parsedTiers) {
      if (quantity >= tier.min && quantity <= tier.max) {
        return tier;
      }
    }
    
    // If quantity exceeds all tiers, use the highest tier
    return parsedTiers[parsedTiers.length - 1];
  },

  // Quick add single unit to quote (incremental)
  quickAddToQuote: (productId, categoryId) => {
    const {
      organizedProducts,
      cartItems,
      priceMode
    } = get();
    
    // Find the product
    const products = organizedProducts[categoryId] || [];
    const product = products.find(p => p.id === productId);
    
    if (!product) {
      console.error('Product not found:', productId);
      return;
    }
    
    // Check if this product already exists in cart
    const existingItemIndex = cartItems.findIndex(item => 
      item.product.id === productId && item.category === categoryId
    );
    
    if (existingItemIndex >= 0) {
      // Increment existing item quantity
      const existingItem = cartItems[existingItemIndex];
      const newQuantity = existingItem.quantity + 1;
      
      // Find optimal pricing tier for new quantity
      const newTier = get().determineOptimalTier(product, newQuantity);
      
      if (newTier) {
        // Get price based on current price mode
        const newUnitPrice = priceMode === 'partner' ? newTier.partnerPrice : newTier.clientPrice;
        
        // Create updated cart item
        const updatedItem = {
          ...existingItem,
          quantity: newQuantity,
          quantityTierId: newTier.id,
          unitPrice: newUnitPrice,
          totalPrice: newUnitPrice * newQuantity,
          tierName: newTier.name,
          priceModeUsed: priceMode,
          calculatedLength: categoryId === 'poles' ? (get().calculatedLengthInfo?.calculatedLength || existingItem.calculatedLength || null) : null
        };
        
        // Replace existing item with updated item
        set(state => ({
          cartItems: [
            ...state.cartItems.slice(0, existingItemIndex),
            updatedItem,
            ...state.cartItems.slice(existingItemIndex + 1)
          ]
        }));
      }
    } else {
      // Add new item with quantity 1
      const firstTier = product.quantities && product.quantities.length > 0 
        ? product.quantities[0] 
        : null;
      
      if (firstTier) {
        const unitPrice = priceMode === 'partner' ? firstTier.partnerPrice : firstTier.clientPrice;
        
        const newCartItem = {
          id: Date.now().toString(),
          product,
          quantityTierId: firstTier.id,
          quantity: 1,
          unitPrice: unitPrice,
          totalPrice: unitPrice,
          category: categoryId,
          attributes: {...product.attributes},
          tierName: firstTier.name,
          priceModeUsed: priceMode,
          calculatedLength: categoryId === 'poles' ? (get().calculatedLengthInfo?.calculatedLength || null) : null
        };
        
        // Add new item to cart
        set(state => ({
          cartItems: [...state.cartItems, newCartItem]
        }));
      }
    }
    
    // Recalculate cart totals
    get().calculateCartTotal();
  },

  // Add the current selection to quote
  addToQuote: () => {
    const {
      selectedProduct,
      selectedQuantity,
      selectedMainCategory,
      organizedProducts,
      cartItems,
      quote,
      priceMode // Destructure priceMode here
    } = get();
    
    if (!selectedProduct || !selectedMainCategory || !selectedQuantity || !quote.lineItems?.length) {
      return;
    }
    
    // Find the selected product
    const products = organizedProducts[selectedMainCategory] || [];
    const product = products.find(p => p.id === selectedProduct);
    
    if (!product) {
      return;
    }
    
    // Get the current line item with exact quantity and pricing
    const currentItem = quote.lineItems[0];
    
    // Find the quantity tier info
    const quantityTier = product.quantities.find(q => q.id === selectedQuantity);
    if (!quantityTier) {
      return;
    }
    
    // Check if this item already exists in the cart
    const existingItemIndex = cartItems.findIndex(item => 
      item.product.id === selectedProduct &&
      item.quantityTierId === selectedQuantity &&
      item.quantity === currentItem.quantity
    );
    
    // Create new cart item with exact values from the quote
    const newCartItem = {
      id: Date.now().toString(),
      product,
      quantityTierId: selectedQuantity,
      quantity: currentItem.quantity,
      unitPrice: currentItem.price,
      totalPrice: currentItem.totalPrice,
      category: selectedMainCategory,
      attributes: {...product.attributes},
      tierName: quantityTier.name,
      priceModeUsed: priceMode, // Use the destructured priceMode variable
      calculatedLength: selectedMainCategory === 'poles' ? (get().calculatedLengthInfo?.calculatedLength || null) : null // Add calculated length only for poles
    };
    
    // Update state
    if (existingItemIndex >= 0) {
      // Replace existing item
      set(state => ({
        cartItems: [
          ...state.cartItems.slice(0, existingItemIndex),
          newCartItem,
          ...state.cartItems.slice(existingItemIndex + 1)
        ]
      }));
    } else {
      // Add new item
      set(state => ({
        cartItems: [...state.cartItems, newCartItem]
      }));
    }
    
    // Calculate total quote from all cart items
    get().calculateCartTotal();
  },
  
  // Remove an item from the cart/quote
  removeFromQuote: (itemId) => {
    set(state => ({
      cartItems: state.cartItems.filter(item => item.id !== itemId)
    }));
    
    // Recalculate quote after removing item
    get().calculateCartTotal();
  },
  
  // Calculate total from all cart items
  calculateCartTotal: () => {
    const { cartItems } = get();
    
    if (!cartItems.length) {
      set({
        quote: {
          subtotal: 0,
          tax: 0,
          total: 0,
          lineItems: []
        }
      });
      return;
    }
    
    // Calculate totals from cart items
    const subtotal = cartItems.reduce((sum, item) => sum + item.totalPrice, 0);
    const tax = subtotal * 0.21; // 21% VAT
    const total = subtotal + tax;
    
    // Create line items for the quote display
    const lineItems = cartItems.map(item => ({
      id: item.id,
      name: item.product.name,
      price: item.unitPrice,
      quantity: item.quantity,
      totalPrice: item.totalPrice,
      category: item.category,
      product: item.product,
      tierName: item.tierName
    }));
    
    // Update quote in store
    set({
      quote: {
        subtotal,
        tax,
        total,
        lineItems
      }
    });
  },

  // Clear recent copy success notification
  clearCopySuccess: () => {
    set({ recentCopySuccess: null });
  },

  // Load items from client protection request into cart
  loadItemsFromProtection: async (protectionItems, confirmClearCart = true) => {
    const { cartItems, organizedProducts, priceMode } = get();
    
    // Return early if no items provided
    if (!protectionItems || !Array.isArray(protectionItems) || protectionItems.length === 0) {
      console.warn('No protection items provided to load');
      return { success: false, message: 'No items to copy' };
    }
    
    // Check if user wants to clear existing cart (when items exist)
    if (confirmClearCart && cartItems.length > 0) {
      const userConfirmed = await useModalStore.getState().showConfirmation({
        title: "Replace Current Quote?",
        message: `You have ${cartItems.length} item${cartItems.length > 1 ? 's' : ''} in your current quote. Loading items from client protection will replace your current quote and navigate to the quote builder.`,
        confirmText: "Yes, replace and navigate to quote",
        cancelText: "Cancel",
        confirmButtonPrimary: true
      });
      
      if (!userConfirmed) {
        return { success: false, message: 'Operation cancelled by user' };
      }
    }
    
    const convertedItems = [];
    const warnings = [];
    
    // Convert each protection item to cart item format
    protectionItems.forEach((item, index) => {
      try {
        const product = item.product;
        if (!product || !product.id) {
          warnings.push(`Item ${index + 1}: Missing product data`);
          return;
        }
        
        // Find the product in current organized products to validate it still exists
        const categoryProducts = organizedProducts[item.category] || [];
        const currentProduct = categoryProducts.find(p => p.id === product.id);
        
        if (!currentProduct) {
          warnings.push(`Item ${index + 1}: Product "${product.name}" no longer available`);
          return;
        }
        
        // Determine appropriate quantity tier for the item's quantity
        const optimalTier = get().determineOptimalTier(currentProduct, item.quantity);
        
        if (!optimalTier) {
          warnings.push(`Item ${index + 1}: No pricing tier available for quantity ${item.quantity}`);
          return;
        }
        
        // Calculate price based on current price mode
        const unitPrice = priceMode === 'partner' ? optimalTier.partnerPrice : optimalTier.clientPrice;
        const totalPrice = unitPrice * item.quantity;
        
        // Create cart item with converted data
        const cartItem = {
          id: `copied_${Date.now()}_${index}`,
          product: currentProduct, // Use current product data
          quantityTierId: optimalTier.id,
          quantity: item.quantity,
          unitPrice: unitPrice,
          totalPrice: totalPrice,
          category: item.category,
          attributes: { ...item.attributes },
          tierName: optimalTier.name,
          priceModeUsed: priceMode,
          calculatedLength: item.calculatedLength || null
        };
        
        convertedItems.push(cartItem);
        
      } catch (error) {
        console.error(`Error converting protection item ${index + 1}:`, error);
        warnings.push(`Item ${index + 1}: Conversion error - ${error.message}`);
      }
    });
    
    // Update cart items with converted items
    set({ cartItems: convertedItems });
    
    // Recalculate cart totals
    get().calculateCartTotal();
    
    // Prepare result message
    const successCount = convertedItems.length;
    const totalCount = protectionItems.length;
    
    let message = '';
    if (successCount === totalCount) {
      message = `Successfully copied ${successCount} item${successCount > 1 ? 's' : ''} to quote`;
    } else {
      message = `Copied ${successCount} of ${totalCount} items. ${totalCount - successCount} items could not be copied.`;
    }
    
    // Set recent copy success for showing notification
    if (successCount > 0) {
      set({ 
        recentCopySuccess: {
          copiedCount: successCount,
          totalCount: totalCount,
          timestamp: Date.now()
        }
      });
    }

    return {
      success: successCount > 0,
      message,
      warnings,
      copiedCount: successCount,
      totalCount: totalCount,
      shouldNavigate: true // Always navigate - if user got this far, they want to go to quote
    };
  }
}));

export default useConfiguratorStore;
